// @flow
//#region imports
import React, { PureComponent, type Node } from "react";
import { History } from "history";
import { withRouter } from "react-router";
import TogglesHandler from "containers/TogglesHandler";
import "./index.css";
import { connect, Dispatch } from "react-redux";
import { compose } from "redux";
import queryString from "data/queryString";
import MainLayout from "components/MainLayout";
import FormContainer from "containers/Form";
import DocViewerContainer from "containers/DocViewer";
import ActivitiesContainer from "containers/Activities";
import {
    setCurrentItemAction,
    setCurrentBreadcrumb,
    backupCurrentItemAction,
    restoreCurrentItemAction,
    formSetEditModeAction,
} from "data/actions";
import {
    ItemStatuses,
    RefreshMode,
    ServerEventItemType,
    ServerEventActionType,
    type DocInfoResultDTO,
    type ItemStatusesDTO,
    type SiteTypeEnum,
    type ToggleDTO,
    type ToggleItemDTO,
} from "data/types";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import CommandActionHandler from "components/CommandActions";
import bowser from "data/bowser";
import * as s from "data/reducers/selectors";
import Constants, { isMiniView } from "data/constants";
import SplitPanes from "containers/SplitPanes";
import loc from "i18next";
// import FloatingBarList from "components/FloatingBar/FloatingBarList";
import FileDropHandler, { type DroppedFile } from "containers/FileDropHandler";
import { toastStyled, toastTypes } from "data/toast";
import {
    check_isSyncUpload,
    check_licenseAllowEdit,
} from "containers/ActionPanel_shouldBeVisible";

import SuggestionHandler from "containers/SuggestionHandler";
import Pane from "components/SplitPanes/Pane";
import Status from "components/Status";
//#endregion

//#region flow types
type Props = {
    /** React-router History */
    history: History,
    /** whether shown as Modal */
    asModal?: boolean,
    /** React Node to show before toolbar */
    topRowBefore?: Node,
    /** React Node to show after toolbar */
    topRowEnd?: Node,
    /** callback when user wants to see previous doc */
    onPrev?: Function,
    /** callback when user wants to see next doc */
    onNext?: Function,
    // from TogglesHandler
    /** TogglesHandler: set current toggles */
    setToggles: Function,
    /** TogglesHandler: current toggles */
    toggles: ?ToggleDTO,
    /** Redux: current itemUri */
    itemUri: string,
    /** Redux: breadcrumb itemUri version */
    version?: number,
    /** whether current document is a favorite */
    isFav: boolean,
    /** whether running in Mini view mode */
    isMini: boolean,
    /** current document name */
    docName: string,
    /** current viewName */
    viewName: string,
    /** whether running under Windows OS */
    onWindows: boolean,
    /** whether trigger edit mode on mount  */
    triggerEditMode: boolean,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** TogglesHandler: currently selected toggles' names */
    selectedToggles: Array<string>,
    /** SplitPanes callback to set split sizes */
    setSplitSizes: Function,
    /** whether folder classification is in edit mode */
    formInEditMode: boolean,
    /**redux: current folder formatId */
    formatId: number,
    /**redux: current folder siteType */
    siteType: SiteTypeEnum,
    /**pdf.js: search terms */
    searchTerm?: string,
    /** pdf.js: whether trigger browser print */
    triggerPrint: boolean,
};

type State = {
    isFlatAU: ?boolean,
    isDocEditAllowed: boolean, //content editing
    isDocClassificationEditAllowed: boolean, //classification editing
    itemStatus: ItemStatusesDTO,
    currentVersion: number,
    currentVersionItemUri: ?string,
    triggerEditMode: boolean,
};
//#endregion

export class Document extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isFlatAU: undefined,
            isDocEditAllowed: false,
            isDocClassificationEditAllowed: false,
            itemStatus: ItemStatuses.Nothing,
            currentVersion: props.version || 0,
            currentVersionItemUri: null,
            triggerEditMode: props.triggerEditMode || false,
        };
    }

    componentDidMount() {
        const { dispatch, itemUri, viewName } = this.props;
        dispatch(backupCurrentItemAction());
        dispatch(setCurrentItemAction({ name: viewName, itemUri }));
    }

    componentWillUnmount() {
        const { dispatch, viewName } = this.props;
        dispatch(setCurrentBreadcrumb({ breadcrumb: {}, viewName }));
        dispatch(formSetEditModeAction("doc", false));
        dispatch(restoreCurrentItemAction());
    }

    componentDidUpdate(prevProps: Props) {
        const { dispatch, viewName } = this.props;
        /* istanbul ignore else */
        if (
            prevProps.itemUri !== this.props.itemUri ||
            prevProps.refreshId !== this.props.refreshId
        ) {
            this.setState({
                isFlatAU: undefined,
                currentVersion: this.props.version || 0,
                currentVersionItemUri: null,
                triggerEditMode: this.props.triggerEditMode || false,
            });
            dispatch(formSetEditModeAction("doc", false));
            dispatch(
                setCurrentItemAction({
                    name: viewName,
                    itemUri: this.props.itemUri,
                })
            );
        }
        if (prevProps.version !== this.props.version) {
            this.setState({
                currentVersion: this.props.version,
            });
        }
    }

    _uploadVersion = (files?: Array<DroppedFile>) =>
        CommandActionHandler("doc_uploadVersion", {
            files,
            itemUri: this.props.itemUri,
        });

    _maybeTriggerEditMode = () => {
        /* istanbul ignore else */
        if (this.state.triggerEditMode) {
            const { dispatch } = this.props;
            if (this.state.isDocClassificationEditAllowed) {
                /* istanbul ignore else */
                if (!this.props.isEditMode) {
                    dispatch(formSetEditModeAction("doc", true));
                    this.setState({
                        triggerEditMode: false,
                    });
                }
            } else {
                toastStyled({
                    message: loc.t("document:edit_form.notAuthorized"),
                    ...toastTypes.failure,
                });
            }
        }
    };

    _onDocInfoResult = (result: DocInfoResultDTO): void => {
        this.setState(
            {
                isFlatAU: result.isFlatAU,
                itemStatus: result.itemStatus,
                isDocEditAllowed:
                    s.docsFlagsIsDocEditAllowedSelector({
                        docs: { status: Constants.OK, flags: result.flags },
                    }) && !this._isRecycleBin(result),
                isDocClassificationEditAllowed:
                    s.docsFlagsIsDocClassificationEditAllowedSelector({
                        docs: { status: Constants.OK, flags: result.flags },
                    }) && !this._isRecycleBin(result),
            },
            this._maybeTriggerEditMode
        );
        /* istanbul ignore else */
        if (result.isFlatAU) {
            // disable form/actions toggle when isFlatAU
            /* istanbul ignore else */
            if (this.props.toggles != null) {
                this.props.setToggles({
                    name: this.props.viewName,
                    isMultiple: true,
                    toggles: this.props.toggles.toggles
                        .filter(
                            (toggle: ToggleItemDTO) => toggle.value === "viewer"
                        )
                        .map((toggle: ToggleItemDTO) => {
                            /* istanbul ignore else */
                            if (result.isFlatAU && toggle.value === "viewer") {
                                toggle.isSelected = true;
                            }
                            return toggle;
                        }),
                });
            }
            this.props.setSplitSizes(1, [0, 100]);
        }
    };

    _onCancelForm = () =>
        this.props.dispatch(formSetEditModeAction("doc", false));

    _onShowVersion = (currentVersion: number, itemUri: string) =>
        this.setState({ currentVersion, currentVersionItemUri: itemUri });

    _isDeleted = (state: State = this.state): boolean =>
        (state.itemStatus & ItemStatuses.Deleted) > 0;
    _isParentDeleted = (state: State = this.state): boolean =>
        (state.itemStatus & ItemStatuses.ParentDeleted) > 0;
    _isRecycleBin = (state: State = this.state): boolean =>
        this._isDeleted(state) || this._isParentDeleted(state);

    render() {
        const { toggles, viewName, searchTerm, triggerPrint } = this.props;
        const itemUriWithVersion =
            this.state.currentVersionItemUri || this.props.itemUri;
        if (this.props.isMini && this._isRecycleBin()) {
            return (
                <Status
                    status={Constants.ERROR}
                    message={loc.t("doc_deleted")}
                />
            );
        }
        return (
            <MainLayout
                name={viewName}
                toggles={toggles}
                contextProps={{
                    itemUri: itemUriWithVersion,
                    formatId: this.props.formatId,
                    isDoc: true,
                    name: this.props.docName,
                    isFlatAU: this.state.isFlatAU,
                    isDocEditAllowed: this.state.isDocEditAllowed,
                    isDocClassificationEditAllowed:
                        this.state.isDocClassificationEditAllowed,
                    itemStatus: this.state.itemStatus,
                    siteType: this.props.siteType,
                }}
                itemUri={itemUriWithVersion}
                actionsDisabled={
                    this.props.formInEditMode ||
                    (this._isParentDeleted() && !this._isDeleted())
                }
                breadcrumbIsRecycleBin={this._isRecycleBin()}
                breadcrumbReadonly={
                    this.props.asModal === true || this.props.isMini === true
                }
                topRowBefore={this.props.topRowBefore}
                topRowEnd={this.props.topRowEnd}
                className={this.state.isFlatAU ? "flatAU" : ""}
            >
                {/* {this._renderFloatingBar()} */}
                <Pane
                    id="viewerPanel"
                    dataTest="viewerPanel"
                    className="d-split"
                >
                    <DocViewerContainer
                        itemUri={itemUriWithVersion}
                        searchTerm={searchTerm}
                        triggerPrint={triggerPrint}
                        onDocInfoResult={this._onDocInfoResult}
                        onPrev={this.props.onPrev}
                        onNext={this.props.onNext}
                    />
                </Pane>
                <div
                    data-test="documentRightPanel"
                    id="drightPanel"
                    className="d-split"
                >
                    <Pane id="docformPanel">
                        {this.state.isFlatAU === false && (
                            <FileDropHandler
                                addFilesToUppy={false}
                                relativePath={itemUriWithVersion}
                                allowDrop={
                                    check_licenseAllowEdit() &&
                                    !check_isSyncUpload({
                                        itemUri: itemUriWithVersion,
                                        formatId: this.props.formatId,
                                    }) &&
                                    !this.props.formInEditMode &&
                                    this.state.isDocEditAllowed
                                }
                                onDrop={this._uploadVersion}
                                dragOverText={loc.t(
                                    "itemActions:doc_uploadVersion.name"
                                )}
                                className="filedrop-handler"
                            >
                                <FormContainer
                                    itemUri={itemUriWithVersion}
                                    viewName={viewName}
                                    isEditMode={this.props.formInEditMode}
                                    onCancel={this._onCancelForm}
                                />
                            </FileDropHandler>
                        )}
                    </Pane>
                    <Pane id="docActivityPanel">
                        {this.state.isFlatAU === false && (
                            <ActivitiesContainer
                                key="activities"
                                itemUri={this.props.itemUri}
                                formatId={this.props.formatId}
                                currentVersion={this.state.currentVersion}
                                onShowVersion={this._onShowVersion}
                                isReadOnly={!!this._isRecycleBin()}
                            />
                        )}
                    </Pane>
                </div>
            </MainLayout>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => {
    const viewName = ownProps.asModal === true ? "docmodal" : "document";
    let searchTerm = ownProps.searchTerm;
    let triggerPrint = ownProps.triggerPrint;
    const qs = queryString.parse(ownProps.history.location.search);
    if (qs.mark != null) {
        searchTerm = qs.mark;
    }
    if (qs.print) {
        triggerPrint = true;
    }
    return {
        itemUri: ownProps.itemUri || s.itemUriSelector(ownProps),
        formatId: s.breadcrumbFormatIdSelector(state, viewName),
        siteType: s.breadcrumbSiteTypeSelector(state, viewName),
        version: s.breadcrumbVersionSelector(state, viewName),
        isFav: s.breadcrumbIsFavSelector(state, viewName),
        isMini: isMiniView(ownProps.history.location),
        docName: s.breadcrumbNameSelector(state, viewName),
        onWindows: bowser.is("windows"),
        viewName,
        formInEditMode: s.formIsEditModeSelector(state, viewName),
        ...ownProps,
        searchTerm,
        triggerPrint,
    };
};

export const DocumentWithoutHash = compose(
    withRouter,
    connect(mapStateToProps),
    TogglesHandler({
        /* viewName */
    }),
    GlobalEventsHandler({
        items: [ServerEventItemType.document],
        actions: [ServerEventActionType.delete],
        refreshMode: RefreshMode.Originator,
        refreshMessage: loc.t("globalEvents:document.delete"),
        refreshRoute: false,
    }),
    SplitPanes({
        /* viewName: "docmodal", */
        paneIds: [
            "#viewerPanel",
            "#drightPanel",
            "#docformPanel",
            "#docActivityPanel",
        ],
        toggleIds: ["viewer", "form", "act"],
    })
)(Document);

export default compose(
    withRouter,
    connect(mapStateToProps),
    SuggestionHandler({ docSearchProvider: true }),
    TogglesHandler({
        /* viewName */
    }),
    GlobalEventsHandler({
        items: [ServerEventItemType.document],
        actions: [ServerEventActionType.delete],
        refreshMode: RefreshMode.Originator,
        refreshMessage: loc.t("globalEvents:document.delete"),
        refreshRoute: false,
    }),
    SplitPanes({
        /*viewName: "docmodal", */
        paneIds: [
            "#viewerPanel",
            "#drightPanel",
            "#docformPanel",
            "#docActivityPanel",
        ],
        toggleIds: ["viewer", "form", "act"],
    })
)(Document);
